import React, { useState, useEffect } from 'react';
import { Text, View, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import InputSection from '@/components/General/inputs/InputSection';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ToggleInput from '@/components/General/inputs/ToggleInput';
import { saveBoard } from '@/data/connect';
import { choosePhoto, getImageSource } from '@/utils/FileManager';
import ResourceCard from '@/components/General/ResourceCard';
import * as Validator from '@/utils/InputValidator';
import Toast from 'react-native-toast-message';
import { useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import ModalWrapper from '@/components/General/ModalWrapper';
import Unicon from '@/components/General/Unicon';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

const boardPermissions = {
    "Admin": "isAdmin",
    "Post": "canPost", 
    "React": "canReact", 
    "Comment": "canComment",
}

const EMPTY_DRAFT = {};

export default function AddBoardModal() {
    const {id, type} = useLocalSearchParams();
    const storeKey = id || "new"

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const boardDetails = useEditScreenStore(s => s.AddBoardModal?.[storeKey]) || EMPTY_DRAFT

    
    /*** State ***/
    const [ newHeroImage, setNewHeroImage ] = useState(null)
    const [ showPermissionInstructions, setShowPermissionInstructions ] = useState(false)


    /*** Theme & Dimensions ***/
    const router = useRouter()
    const pathname = usePathname()


    /*** Queries ***/
    const queryClient = useQueryClient()
    const { data: loggedInUser } = Query.people.loggedInUser()
    const { data: boardToEdit } = Query.boards.byId({id, options: {enabled: !!id}})

    const saveBoardMutation = useMutation({
        mutationFn: saveBoard,
        onSuccess: async () => {
            removeEditScreenData("AddBoardModal", storeKey)
            await queryClient.invalidateQueries({queryKey: QueryKeys.boards.all()});
            if(id) await queryClient.invalidateQueries({queryKey: QueryKeys.boards.byId(id)});
            router.back();      
        }
    })


    useEffect(() => {
        if(boardToEdit){
            setBoardDetails({
                ...boardToEdit,
                Roles: boardToEdit.Roles.map(r => ({
                    ...r.Role,
                    canPost: r.canPost,
                    canReact: r.canReact,
                    canComment: r.canComment,
                    isAdmin: r.isAdmin,
                }))
            });
        }
    }, [boardToEdit]);

    const setBoardDetails = (details) => {
        setEditScreenData("AddBoardModal", storeKey, details)
    }


    const setPermission = (role, permissionToChange, new_value = null) => {
        const newRoles = [...boardDetails.Roles];
        const currentRoleIndex = newRoles.findIndex(n => n.id == role.id);

        newRoles[currentRoleIndex][permissionToChange] = new_value === null ? !newRoles[currentRoleIndex][permissionToChange] : new_value;

        setBoardDetails({
            Roles: newRoles
        })
    }

    const setAllPermissions = (role, newValue) => {
        const newRoles = [...boardDetails.Roles];
        const currentRoleIndex = newRoles.findIndex(n => n.id == role.id);

        Object.values(boardPermissions).forEach(permission => {
            newRoles[currentRoleIndex][permission] = newValue;
        });

        setBoardDetails({
            Roles: newRoles
        })
    }

    const roleOptions = boardDetails.Roles?.map((role) => {
        const selectedNames = Object.entries(boardPermissions).map(([displayName, name]) => {
            return role[name] ? displayName : undefined
        });

        return <ToggleInput
            key={role.name}
            title={role.name}
            enableSelectAll
            items={Object.keys(boardPermissions)}
            selectedValues={selectedNames}
            onItemChanged={(selectedItem, new_value) => {
                setPermission(role, boardPermissions[selectedItem], new_value)
            }}
            onSelectAll={(_, newValue) => {
                setAllPermissions(role, newValue)
            }}
            itemsPerRow={4}
        />
    });

    const chooseHeroImage = async () => {
        const assets = await choosePhoto({noVideos: true, maxSize: 1600, quality: 0.3})

        if(assets?.length > 0){
            setNewHeroImage(assets[0])
        }
    }
    const existingHeroPath = boardDetails.avatarPath
    const heroImageSource = newHeroImage?.uri
        ? {uri: newHeroImage.uri}
        : getImageSource(existingHeroPath, existingHeroPath ? `board-${boardDetails.id || id}-${existingHeroPath}` : undefined)

    return (
        <ModalWrapper 
            loading={saveBoardMutation.isPending} 
            onConfirm={() => {
                if(!Validator.areStringsPopulated([boardDetails.name, boardDetails.description])){
                    Toast.show({type:'error', text2:"A new board requires a name and a description!"})
                    return false
                }
                if(!id && !loggedInUser?.id){
                    Toast.show({type:'error', text2:"Still loading your profile — try again in a moment"})
                    return false
                }

                saveBoardMutation.mutate({details: {...boardDetails, heroImageFile: newHeroImage?.uri}, type: type || "MESSAGE_BOARD", creator: loggedInUser?.id})
            }} 
            title={`${id ? "Update" : "New"} Board`}
        >
        
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled">
                <View style={styles.scrollContent}>
                    
                    <InputSection title={`Board Info`} description={`Basic info to identify the Board's purpose`}>
                        <View style={styles.heroImageField}>
                            <View style={styles.heroImageHeader}>
                                <View style={styles.heroImageCopy}>
                                    <Text style={styles.heroImageLabel}>Hero image</Text>
                                    <Text style={styles.heroImageHelper}>Optional; recommended wide landscape image.</Text>
                                </View>
                                <Pressable accessibilityRole="button" onPress={chooseHeroImage} testID="BoardHeroImageChooseButton">
                                    <Text style={styles.heroImageAction}>{existingHeroPath || newHeroImage ? "Change image" : "Choose image"}</Text>
                                </Pressable>
                            </View>
                            <ResourceCard
                                variant="hero"
                                heroImage={heroImageSource}
                                heroSeed={boardDetails.id || boardDetails.name || "new-board"}
                                eyebrow="Board preview"
                                title={boardDetails.name || "Board name"}
                                subtitle={boardDetails.description || "Your board description will appear here."}
                                moduleColor="connect"
                                onPress={chooseHeroImage}
                                style={styles.heroImagePreview}
                                testID="BoardHeroImagePicker"
                            />
                            {newHeroImage?.uri &&
                                <Pressable accessibilityRole="button" onPress={() => setNewHeroImage(null)} testID="BoardHeroImageClearButton" style={styles.clearHeroImageButton}>
                                    <Text style={styles.clearHeroImageText}>Clear selection</Text>
                                </Pressable>
                            }
                        </View>

                        <Input
                            title="Name"
                            value={boardDetails.name}
                            maxLength={120}
                            onUpdate={(new_value) => {
                                setBoardDetails({
                                    ...boardDetails,
                                    name: new_value
                                })
                            }}
                        />


                        <Input
                            title="Description"
                            value={boardDetails.description}
                            multiline
                            onUpdate={(new_value) => {
                                setBoardDetails({
                                    ...boardDetails,
                                    description: new_value
                                })
                            }}
                        />
                    </InputSection>

                    <InputSection title="Roles" description={`People in the selected Roles will have access to see posts within this Board. You can modify permissions on this board for each role as well.`}>
                        <SheetSelector multiple type="roles" title="Roles" fromScreen={pathname} fromStoreKey={storeKey} value={boardDetails.Roles || []} onChange={(selected_items) => {
                            setBoardDetails({
                                    ...boardDetails,
                                    Roles: selected_items
                                })
                            }} 
                        />

                        {roleOptions?.length > 0 &&
                            <View style={styles.rolesContainer}>
                                <Pressable onPress={() => setShowPermissionInstructions(true)} style={styles.permissionsHeader}>
                                    <Text style={styles.title}>Permissions</Text>
                                    <Unicon name="information-circle-outline" size={22} colorVariant="textSecondary"/>
                                </Pressable>
                                <Text style={styles.description}>{`Define what members of each role can do in this Board. All roles are read-only by default, select the permissions you'd like the members for each role to have`}</Text>
                            </View>
                        }

                        {roleOptions}
                    </InputSection>

                </View>
            </KeyboardAwareScrollView>

            {showPermissionInstructions &&
                <Pressable style={styles.container} onPress={() => setShowPermissionInstructions(false) }>
                    <View style={styles.modalContainer}>
                        <View style={styles.closeContainer}><Unicon name="close-outline" size={22} colorVariant="text"/></View>
                        <View style={styles.permissionContainer}>
                            <Text style={styles.permissionTitle}>Admin</Text>
                            <Text style={styles.permissionDescription}>Allows members in the given role to edit the basic role details like name & description, as well as add and remove Roles. They will also have the ability to moderate content with the ability to delete comments & posts.</Text>
                        </View>
                        <View style={styles.permissionContainer}>
                            <Text style={styles.permissionTitle}>Post</Text>
                            <Text style={styles.permissionDescription}>This gives members in the given role the ability to create new posts in this Board. Creators will have the ability to edit their own post as well.</Text>
                        </View>
                        <View style={styles.permissionContainer}>
                            <Text style={styles.permissionTitle}>Comment</Text>
                            <Text style={styles.permissionDescription}>This allows members to comment on and reply to others&apos; posts. Disabling this will not allow the specified role to comment on any post in the Board.</Text>
                        </View>
                        <View style={styles.permissionContainer}>
                            <Text style={styles.permissionTitle}>React</Text>
                            <Text style={styles.permissionDescription}>This allows members to react to others&apos; posts. Disabling this will not allow the specified role to use reactions on any post in the Board</Text>
                        </View>
                    </View>
                </Pressable>
            }

        </ModalWrapper> 
    );
}


const styles = StyleSheet.create(({colors, fonts}, rt) => ({
    permissionTitle: {
        fontSize:16, 
        color:colors.text, 
        fontFamily:fonts.bold, 
        marginBottom:10
    },
    permissionDescription: {
        fontSize:14, 
        color:colors.text, 
        fontFamily:fonts.body
    },
    permissionContainer: {
        borderBottomWidth:1, 
        borderBottomColor:colors.border,
        paddingVertical:20
    },
    modalContainer: {
        width:400, 
        padding:20, 
        boxShadow: "0px 0px 15px -8px rgba(0, 0, 0, 0.2)", 
        borderRadius:8, 
        backgroundColor:colors.background, 
        position:'fixed', 
        top:"50%", 
        left:"50%", 
        transform:[{translateX:"-50%"}, {translateY:"-50%"}] 
    },
    container: {
        backgroundColor:"rgba(0,0,0,0.5)", 
        position:"absolute", 
        width:rt.screen.width, 
        height:rt.screen.height, 
        alignItems:"center", 
        justifyContent:"center", 
        zIndex:10000
    },
    description: {
        fontStyle:"italic", 
        fontSize:14,
        color:colors.textSecondary
    },
    title: {
        fontSize:18,
        color:colors.text,
        fontFamily:fonts.heading,
        paddingBottom:5,
        marginRight:5
    },
    rolesContainer: {
        marginTop:20
    },
    heroImageField: {
        gap: 10,
        marginBottom: 20
    },
    heroImageHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 15
    },
    heroImageCopy: {
        flex: 1,
        gap: 3
    },
    heroImageLabel: {
        color: colors.text,
        fontFamily: fonts.heading,
        fontSize: 16
    },
    heroImageHelper: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 13
    },
    heroImageAction: {
        color: colors.connect,
        fontFamily: fonts.bold,
        fontSize: 14
    },
    heroImagePreview: {
        flexGrow: 0,
        minHeight: 200
    },
    clearHeroImageButton: {
        alignSelf: 'flex-end'
    },
    clearHeroImageText: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 13
    },
    closeContainer: {
        position:"absolute",
        right:15,
        top:15
    },
    scrollContent: {
        paddingBottom:20
    },
    permissionsHeader: {
        flexDirection:'row'
    }
}))